import React, { useCallback, useEffect, useRef } from "react";
import RestaurantCard from "./card";
import RestaurantAd from "./ad";
import "./index.less";

const getDesktopFixedHeaderHeight = () => {
  if (typeof window === "undefined") return 0;
  // Only consider desktop; mobile head-bar is already accounted for via padding.
  if (window.innerWidth <= 768) return 0;

  const header =
    document.querySelector(".ui-head-bar") ||
    document.querySelector(".header-bar") ||
    document.querySelector("header");

  if (!header) return 0;

  const style = window.getComputedStyle(header);
  if (style.position !== "fixed" && style.position !== "sticky") return 0;

  return Math.ceil(header.getBoundingClientRect().height || 0);
};

const getExploreScrollContainer = () => {
  // Explore page uses its own scroll container.
  return document.querySelector(".expore-page-list");
};

const getDocumentScrollElement = () => {
  return document.scrollingElement || document.documentElement || document.body;
};

const scrollToTop = () => {
  const exploreEl = getExploreScrollContainer();
  if (exploreEl && typeof exploreEl.scrollTo === "function") {
    exploreEl.scrollTo({ top: 0, left: 0, behavior: "auto" });
    return;
  }
  if (exploreEl) {
    exploreEl.scrollTop = 0;
    return;
  }
  if (typeof window !== "undefined") window.scrollTo({ top: 0, left: 0, behavior: "auto" });
};

const getActiveScrollTarget = () => {
  const exploreEl = getExploreScrollContainer();
  if (exploreEl && exploreEl.scrollHeight - exploreEl.clientHeight > 1) {
    return { el: exploreEl, isDocumentScroll: false };
  }
  return { el: getDocumentScrollElement(), isDocumentScroll: true };
};

export default function RestaurantList({
  restaurants = [],
  loadMore,
  hasMore,
  infiniteKey,
}) {
  const isRequestingRef = useRef(false);
  const loadMoreRef = useRef(loadMore);
  const hasMoreRef = useRef(hasMore);
  const restaurantsLengthRef = useRef(restaurants?.length || 0);
  const requestStartLenRef = useRef(0);
  const requestTimeoutRef = useRef(null);
  const restaurantsLength = restaurants?.length || 0;
  const prevLenRef = useRef(restaurantsLength);

  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
    if (!hasMore) isRequestingRef.current = false;
  }, [hasMore]);

  // When pagination resets (page=1), ExplorePage bumps `infiniteKey`.
  // Scroll back to the top of the active scroll container.
  useEffect(() => {
    setTimeout(() => {
      scrollToTop();
    }, 100)
  }, [infiniteKey]);

  // When list length changes, assume request completed and allow next load.
  useEffect(() => {
    const nextLen = restaurantsLength;
    restaurantsLengthRef.current = nextLen;
    if (nextLen !== prevLenRef.current) {
      prevLenRef.current = nextLen;
      isRequestingRef.current = false;
    }
  }, [restaurantsLength]);

  const checkAndLoadMore = useCallback(() => {
    const hasMoreNow = hasMoreRef.current;
    const loadMoreNow = loadMoreRef.current;

    if (!hasMoreNow) return;
    if (typeof loadMoreNow !== "function") return;
    if (isRequestingRef.current) return;

    const { el: scrollEl, isDocumentScroll } = getActiveScrollTarget();
    if (!scrollEl) return;

    const headerHeight = isDocumentScroll ? getDesktopFixedHeaderHeight() : 0;
    const threshold = 120;

    const scrollTop = isDocumentScroll
      ? (document.scrollingElement?.scrollTop ||
          document.documentElement?.scrollTop ||
          document.body?.scrollTop ||
          0)
      : scrollEl.scrollTop;

    const scrollHeight = isDocumentScroll
      ? (document.scrollingElement?.scrollHeight ||
          document.documentElement?.scrollHeight ||
          document.body?.scrollHeight ||
          0)
      : scrollEl.scrollHeight;

    const clientHeight = isDocumentScroll ? window.innerHeight : scrollEl.clientHeight;
    const visibleHeight = Math.max(0, clientHeight - headerHeight);

    if (scrollTop + visibleHeight + threshold >= scrollHeight) {
      isRequestingRef.current = true;
      requestStartLenRef.current = restaurantsLengthRef.current;

      // Fallback unlock: if list never updates (request error / cancelled), don't lock forever.
      if (requestTimeoutRef.current) window.clearTimeout(requestTimeoutRef.current);
      requestTimeoutRef.current = window.setTimeout(() => {
        if (!isRequestingRef.current) return;
        if (restaurantsLengthRef.current === requestStartLenRef.current) {
          isRequestingRef.current = false;
        }
      }, 5000);

      const result = loadMoreNow();
      // Best-effort unlock for async callers.
      if (result && typeof result.finally === "function") {
        result.finally(() => {
          isRequestingRef.current = false;
        });
      }
    }
  }, []);

  useEffect(() => {
    const exploreEl = getExploreScrollContainer();
    const onScroll = () => checkAndLoadMore();

    // Desktop: sometimes the wheel scrolls the container, sometimes the page.
    // Listen to both to avoid missing events.
    if (exploreEl) exploreEl.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });

    // Desktop header height can change on resize; debounce the recalculation.
    let resizeTimer = null;
    const onResize = () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        checkAndLoadMore();
      }, 150);
    };
    window.addEventListener("resize", onResize, { passive: true });

    // Initial check: in case first page doesn't fill viewport.
    checkAndLoadMore();

    return () => {
      if (exploreEl) exploreEl.removeEventListener("scroll", onScroll);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (resizeTimer) window.clearTimeout(resizeTimer);
      if (requestTimeoutRef.current) window.clearTimeout(requestTimeoutRef.current);
    };
    // Re-bind when the infinite key changes (new search / reset).
  }, [checkAndLoadMore, infiniteKey]);

  return (
    <div className="explore-restaurant-list-wrapper">
      <div className="explore-restaurant-list" key={infiniteKey}>
        {restaurants.map((restaurant, index) => (
          <React.Fragment key={restaurant?.id ?? `idx-${index}`}>
            <RestaurantCard restaurant={restaurant} />
            {restaurant?.ad && <RestaurantAd ad={restaurant.ad} />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
